import { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing, Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import usePushNotifications from "../../hooks/usePushNotifications";
import { usePublicModules } from "../../hooks/useModuleAccess";

export default function PushNotificationPrompt({ user, autoPrompt = true }) {
  const [showDialog, setShowDialog] = useState(false);
  const [hasPrompted, setHasPrompted] = useState(false);
  const { isPublicModuleOn, loading: publicModulesLoading } = usePublicModules();
  const promptModuleOn = !publicModulesLoading && isPublicModuleOn('push_notification_prompt');
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    loading, 
    error,
    subscribe, 
    unsubscribe,
    sendTestNotification,
    resetSubscription
  } = usePushNotifications();

  // AUTO-ON: aktifkan push notification secara automatik (senyap, tanpa dialog)
  // Kecuali: user telah OFF-kan sendiri di tetapan (push_user_disabled) atau kebenaran pelayar disekat
  useEffect(() => {
    if (isSupported && !isSubscribed && permission !== 'denied' && !hasPrompted && user?.id) {
      const userDisabledKey = `push_user_disabled_${user.id}`;
      if (localStorage.getItem(userDisabledKey)) return; // user OFF-kan sendiri - hormati pilihan

      const autoSubKey = `push_auto_sub_active_${user.id}`;
      const active = localStorage.getItem(autoSubKey);
      // Elak instance berganda subscribe serentak
      if (active && Date.now() - parseInt(active) < 30000) return;

      const timer = setTimeout(async () => {
        const current = localStorage.getItem(autoSubKey);
        if (current && Date.now() - parseInt(current) < 30000) return;
        localStorage.setItem(autoSubKey, Date.now().toString());
        setHasPrompted(true);
        try {
          const result = await subscribe();
          if (result.success) {
            console.log('[PUSH] Auto-subscribed successfully');
          }
        } catch (e) {
          console.warn('[PUSH] Auto-subscribe failed:', e);
        } finally {
          localStorage.removeItem(autoSubKey);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSupported, isSubscribed, permission, hasPrompted, user?.id, subscribe]);
  
  // Clean up active dialog flag when dialog closes
  useEffect(() => {
    if (!showDialog && user?.id) {
      localStorage.removeItem(`push_dialog_active_${user?.id}`);
    }
  }, [showDialog, user?.id]);

  // Show for all roles that need notifications
  const allowedRoles = ['ibu_bapa', 'admin', 'superadmin', 'pengurus_cawangan', 'branch_manager'];
  if (!user?.role || !allowedRoles.includes(user.role)) return null;
  if (!promptModuleOn) return null;

  const handleSubscribe = async () => {
    const result = await subscribe();
    if (result.success) {
      toast.success('Notifikasi push diaktifkan! 🔔');
      setShowDialog(false);
      localStorage.removeItem(`push_user_disabled_${user?.id}`);
      localStorage.removeItem(`push_prompt_dismissed_${user?.id}`);
      localStorage.removeItem(`push_dialog_active_${user?.id}`);
      
      // Send test notification after short delay
      setTimeout(async () => {
        await sendTestNotification();
      }, 1500);
    } else {
      toast.error(result.error || 'Gagal mengaktifkan notifikasi');
    }
  };

  const handleUnsubscribe = async () => {
    const result = await unsubscribe();
    if (result.success) {
      // Tanda user OFF-kan sendiri - auto-on tidak akan aktifkan semula
      localStorage.setItem(`push_user_disabled_${user?.id}`, Date.now().toString());
      toast.success('Notifikasi push dinyahaktifkan');
    } else {
      toast.error('Gagal menyahaktifkan notifikasi');
    }
  };

  const handleReset = async () => {
    toast.loading('Menetapkan semula notifikasi...');
    const result = await resetSubscription();
    if (result.success) {
      localStorage.removeItem(`push_user_disabled_${user?.id}`);
      toast.dismiss();
      toast.success('Notifikasi telah diaktifkan semula! 🔔');
      
      // Send test notification
      setTimeout(async () => {
        await sendTestNotification();
      }, 1500);
    } else {
      toast.dismiss();
      toast.error(result.error || 'Gagal menetapkan semula');
    }
  };

  const handleTest = async () => {
    toast.loading('Menghantar notifikasi ujian...');
    const result = await sendTestNotification();
    toast.dismiss();
    if (result.success) {
      toast.success('Notifikasi ujian dihantar! Semak peranti anda.');
    } else {
      toast.error('Gagal menghantar notifikasi ujian');
    }
  };

  // Don't show if not supported
  if (!isSupported) {
    return (
      <span className="flex items-center gap-1 text-xs text-gray-400 bg-gray-100 p-1.5 sm:px-2 sm:py-1 rounded-full" title="Notifikasi tidak disokong">
        <BellOff className="w-3 h-3" />
        <span className="hidden sm:inline">N/A</span>
      </span>
    );
  }

  // Get role-specific content
  const getRoleContent = () => {
    if (user.role === 'superadmin') {
      return {
        gradient: 'from-red-500 to-orange-500',
        hoverGradient: 'from-red-600 to-orange-600',
        title: 'Notifikasi Sistem',
        description: 'Terima notifikasi segera untuk amaran sistem dan isu kritikal.',
        items: [
          '✓ Amaran storan database',
          '✓ Permintaan pengguna baru',
          '✓ Error sistem kritikal',
          '✓ Backup automatik'
        ]
      };
    } else if (['admin', 'branch_manager', 'pengurus_cawangan'].includes(user.role)) {
      return {
        gradient: 'from-blue-500 to-indigo-500',
        hoverGradient: 'from-blue-600 to-indigo-600',
        title: 'Notifikasi Penyelia',
        description: 'Terima notifikasi segera untuk aktiviti penting.',
        items: [
          '✓ Pendaftaran kanak-kanak baru',
          '✓ Kemaskini aktiviti guru',
          '✓ Pembayaran baru',
          '✓ Permintaan ibu bapa'
        ]
      };
    }
    // Default for parents
    return {
      gradient: 'from-pink-500 to-purple-500',
      hoverGradient: 'from-pink-600 to-purple-600',
      title: 'Notifikasi Push',
      description: 'Terima notifikasi segera apabila guru mengemaskini aktiviti anak anda.',
      items: [
        '✓ Aktiviti harian baru',
        '✓ Log harian dikemaskini',
        '✓ Album foto baru',
        '✓ Pengumuman penting'
      ]
    };
  };

  const content = getRoleContent();

  // Show enable button if not subscribed and permission not denied
  if (!isSubscribed && permission !== 'denied') {
    return (
      <>
        <button
          onClick={() => setShowDialog(true)}
          className={`flex items-center gap-1 sm:gap-2 p-2 sm:px-3 sm:py-2 bg-gradient-to-r ${content.gradient} text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all animate-pulse`}
          title="Aktifkan Notifikasi"
        >
          <BellRing className="w-4 h-4" />
          <span className="hidden sm:inline">Aktifkan</span>
        </button>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-pink-500" />
                {content.title}
              </DialogTitle>
              <DialogDescription>
                {content.description}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg p-4 border border-pink-200">
                <h4 className="font-medium text-pink-800 mb-2">Anda akan menerima notifikasi untuk:</h4>
                <ul className="text-sm text-pink-700 space-y-1">
                  {content.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  ⚠️ {error}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDialog(false);
                    localStorage.setItem(`push_prompt_dismissed_${user?.id}`, Date.now().toString());
                    localStorage.removeItem(`push_dialog_active_${user?.id}`);
                  }}
                  className="flex-1"
                >
                  Nanti
                </Button>
                <Button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className={`flex-1 bg-gradient-to-r ${content.gradient} hover:opacity-90 text-white`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Mengaktifkan...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Aktifkan
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Show status if subscribed - NOW shows INFO about notifications they will receive
  if (isSubscribed) {
    return (
      <div className="flex items-center gap-1 sm:gap-2">
        {/* Info button that shows notification list */}
        <button
          onClick={() => setShowDialog(true)}
          className={`flex items-center gap-1 text-xs text-green-600 bg-green-50 p-1.5 sm:px-2 sm:py-1 rounded-full hover:bg-green-100 transition-colors`}
          title="Klik untuk lihat senarai notifikasi"
        >
          <CheckCircle className="w-3 h-3" />
          <span className="hidden sm:inline">Aktif</span>
        </button>
        <button
          onClick={handleReset}
          disabled={loading}
          className="text-xs text-gray-400 hover:text-blue-500 p-1 transition-colors"
          title="Reset notifikasi"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button
          onClick={handleUnsubscribe}
          disabled={loading}
          className="text-xs text-gray-400 hover:text-red-500 p-1 transition-colors"
          title="Nyahaktifkan notifikasi"
        >
          <BellOff className="w-3.5 h-3.5" />
        </button>

        {/* Info Dialog - Shows list of notifications user will receive */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                {content.title} Aktif
              </DialogTitle>
              <DialogDescription>
                Notifikasi push telah diaktifkan. Anda akan menerima pemberitahuan berikut:
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              {/* List of notifications */}
              <div className={`bg-gradient-to-br ${
                user.role === 'superadmin' ? 'from-red-50 to-orange-50 border-orange-200' :
                ['admin', 'branch_manager', 'pengurus_cawangan'].includes(user.role) ? 'from-blue-50 to-indigo-50 border-blue-200' :
                'from-pink-50 to-purple-50 border-pink-200'
              } rounded-lg p-4 border`}>
                <h4 className={`font-medium mb-2 ${
                  user.role === 'superadmin' ? 'text-orange-800' :
                  ['admin', 'branch_manager', 'pengurus_cawangan'].includes(user.role) ? 'text-blue-800' :
                  'text-pink-800'
                }`}>Senarai Notifikasi:</h4>
                <ul className={`text-sm space-y-1.5 ${
                  user.role === 'superadmin' ? 'text-orange-700' :
                  ['admin', 'branch_manager', 'pengurus_cawangan'].includes(user.role) ? 'text-blue-700' :
                  'text-pink-700'
                }`}>
                  {content.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item.replace('✓ ', '')}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={loading}
                  className="flex-1"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Hantar Ujian
                </Button>
                <Button
                  onClick={() => setShowDialog(false)}
                  className={`flex-1 bg-gradient-to-r ${content.gradient} hover:opacity-90 text-white`}
                >
                  Tutup
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Show denied message
  if (permission === 'denied') {
    return (
      <span 
        className="flex items-center gap-1 text-xs text-red-600 bg-red-50 p-1.5 sm:px-2 sm:py-1 rounded-full cursor-help" 
        title="Notifikasi disekat. Sila aktifkan dalam tetapan pelayar anda."
      >
        <XCircle className="w-3 h-3" />
        <span className="hidden sm:inline">Disekat</span>
      </span>
    );
  }

  return null;
}
